/**
 * VE-31538 — which project TDO, if any, the open document should lock.
 *
 * THE RULE: opening any member of a merged document locks the whole set. The
 * app does not sync concurrent edits between members, and a rebuild reads
 * every member, so two reviewers in one set is a lost-work hazard rather than
 * a collaboration feature. Locking the set is the restrictive starting point;
 * narrowing it later means changing who READS this verdict, not how it is
 * taken (see `plans/plan-VE-31538-merged-document-locking.md`).
 *
 * WHY THE PROJECT TDO AND NOT A LOCK PER MEMBER. One record per member would
 * cost two calls per member per heartbeat — 100 a minute for a 50-member set
 * — and gives N chances to leak a lock on an unclean exit instead of one. The
 * project TDO is a real TDO filed in the request, so one record on it says
 * what we mean: this set is in use.
 *
 * SCOPED TO THE OPEN REQUEST, for the same reason `renderedBates` is: project
 * membership is reachable only through the request's folder state, and there
 * is no TDO-to-project index to resolve one in isolation.
 */
import { selectEditorRequestFolder } from '@redact-modules/casemedia/selectors';
import type { TDOId } from '@common-modules/universal/models/Brands';

import { projectOfFolder, selectProjectMembers } from './selectors';

type LockableProjectState = Parameters<typeof selectEditorRequestFolder>[0] &
  Parameters<ReturnType<typeof selectProjectMembers>>[0];

/**
 * The request's merged-document TDO, member or not — "is there a set here at
 * all?".
 *
 * Separate from `selectLockableProjectTdoId` because the two answer different
 * questions and the saga needs both: that one says which project this
 * session may HOLD, while this one says which project there is to ask
 * about. A document outside the set may lock nothing and must still learn
 * who has it, so the ask is driven from here and the take from there.
 */
export const selectEditorProjectTdoId = (
  state: LockableProjectState
): TDOId | undefined =>
  projectOfFolder(selectEditorRequestFolder(state))?.tdoId;

/**
 * The project TDO the open document should hold a lock on, or undefined.
 *
 * Takes the open document's id rather than reading it from the documents
 * slice, which keeps this module free of an import back into `documents` —
 * `documents/sagas/routeSaga` already imports from here.
 *
 * Undefined in three distinct situations, all of which must NOT lock:
 * - the request has no project at all;
 * - its membership has not been read yet (`selectProjectMembers` returns
 *   undefined, which is not the same answer as "no members") — the next pass
 *   picks it up once `LOAD_PROJECT_SUCCESS` lands;
 * - the open document is not IN the set. A request may hold documents that
 *   were never added to its merged document, and looking at one of those must
 *   not lock the set.
 */
export const selectLockableProjectTdoId =
  (openTdoId: string | undefined) =>
  (state: LockableProjectState): TDOId | undefined => {
    if (!openTdoId) {
      return undefined;
    }
    const projectTdoId = projectOfFolder(
      selectEditorRequestFolder(state)
    )?.tdoId;
    if (!projectTdoId) {
      return undefined;
    }
    const members = selectProjectMembers(projectTdoId)(state);
    return members?.some((member) => String(member) === String(openTdoId))
      ? projectTdoId
      : undefined;
  };
